import type { BrowserOptions } from '@sentry/react';
import type { IRootReducerState } from 'store/store';

type BeforeSend = NonNullable<BrowserOptions['beforeSend']>;
type ReadState = () => Pick<
  IRootReducerState,
  'userInfo' | 'submitConfigurationInfo'
>;

// The store is required when an error is sent, not imported: its reducers
// import src/index.tsx, which imports SentryProvider.
const readStoreState: ReadState = () =>
  // eslint-disable-next-line @typescript-eslint/no-var-requires
  (require('store/store') as typeof import('store/store')).store.getState();

// The signed-in Logyx user's id, or none: logged out, or an intent session
// (Blueprint, the dealer panel), whose calls are made with the intent's uuid.
const readUserId = (): number | string | undefined => {
  try {
    const state = readStoreState();
    if (state.submitConfigurationInfo?.intentUUID) {
      return undefined;
    }
    const id: unknown = state.userInfo?.id;
    return typeof id === 'number' || (typeof id === 'string' && id)
      ? id
      : undefined;
  } catch {
    return undefined;
  }
};

// Sentry's beforeSend, for errors only: it sets the event's user to the Logyx
// user's id and nothing else (no e-mail, no name), then runs `beforeSend`. The
// id is read from the redux store, which persists it: it holds after a reload
// and is gone after any logout. Transactions and sessions carry no user.
export const withSentryUser =
  (beforeSend?: BeforeSend): BeforeSend =>
  (event, hint) => {
    const id = readUserId();
    if (id === undefined) {
      delete event.user;
    } else {
      event.user = { id };
    }
    return beforeSend ? beforeSend(event, hint) : event;
  };
